/**
 * MKS-164 — task label query/mutation hooks.
 *
 * Invalidation mirrors `useTaskMutation.ts`'s canonical pair exactly:
 * `['board', projectId, audience]` + `taskActivityQueryKey(taskId)` — the
 * SAME board cache key, never a re-derived one.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listProjectTaskLabels,
  createProjectTaskLabel,
  setTaskLabels as setTaskLabelsApi,
  listProjectSharedTaskLabels,
  setProjectDisabledTaskLabels,
  listGlobalTaskLabels,
  createGlobalTaskLabel,
  updateTaskLabel,
  deleteTaskLabel,
  type TaskLabelRecord,
  type SharedTaskLabelRecord,
  type GlobalTaskLabelRecord,
} from '../services/api/taskLabels';
import { taskActivityQueryKey } from './useTaskActivity';

export function useProjectTaskLabels(projectId: number | null | undefined) {
  return useQuery<TaskLabelRecord[]>({
    queryKey: ['project-task-labels', projectId],
    queryFn: () => listProjectTaskLabels(projectId as number),
    enabled: projectId != null,
  });
}

export interface SetTaskLabelsInput {
  taskId: number;
  projectId: string;
  audience: 'internal' | 'external';
  labelIds: number[];
}

export function useSetTaskLabels() {
  const queryClient = useQueryClient();
  return useMutation<TaskLabelRecord[], Error, SetTaskLabelsInput>({
    mutationFn: (input) => setTaskLabelsApi(input.taskId, input.labelIds),
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: ['board', input.projectId, input.audience] });
      queryClient.invalidateQueries({ queryKey: taskActivityQueryKey(input.taskId) });
    },
  });
}

export interface CreateProjectTaskLabelInput {
  projectId: number;
  name: string;
  color: string;
}

export function useCreateProjectTaskLabel() {
  const queryClient = useQueryClient();
  return useMutation<TaskLabelRecord, Error, CreateProjectTaskLabelInput>({
    mutationFn: (input) =>
      createProjectTaskLabel(input.projectId, { name: input.name, color: input.color }),
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: ['project-task-labels', input.projectId] });
    },
  });
}

// ---------------------------------------------------------------------------
// MKS-406 — project-scoped labels.
// ---------------------------------------------------------------------------

export function useProjectSharedTaskLabels(projectId: number | null | undefined) {
  return useQuery<SharedTaskLabelRecord[]>({
    queryKey: ['project-shared-task-labels', projectId],
    queryFn: () => listProjectSharedTaskLabels(projectId as number),
    enabled: projectId != null,
  });
}

export function useSetProjectDisabledTaskLabels() {
  const queryClient = useQueryClient();
  return useMutation<
    SharedTaskLabelRecord[],
    Error,
    { projectId: number; disabledLabelIds: number[] }
  >({
    mutationFn: (input) => setProjectDisabledTaskLabels(input.projectId, input.disabledLabelIds),
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: ['project-shared-task-labels', input.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-task-labels', input.projectId] });
    },
  });
}

export function useGlobalTaskLabels() {
  return useQuery<GlobalTaskLabelRecord[]>({
    queryKey: ['global-task-labels'],
    queryFn: listGlobalTaskLabels,
  });
}

/** A renamed/recolored/deleted global label changes chips on every board and picker. */
function invalidateGlobalLabelCaches(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['global-task-labels'] });
  queryClient.invalidateQueries({ queryKey: ['project-task-labels'] });
  queryClient.invalidateQueries({ queryKey: ['project-shared-task-labels'] });
  queryClient.invalidateQueries({ queryKey: ['board'] });
}

export function useCreateGlobalTaskLabel() {
  const queryClient = useQueryClient();
  return useMutation<GlobalTaskLabelRecord, Error, { name: string; color: string }>({
    mutationFn: createGlobalTaskLabel,
    onSettled: () => invalidateGlobalLabelCaches(queryClient),
  });
}

export function useUpdateTaskLabel() {
  const queryClient = useQueryClient();
  return useMutation<
    TaskLabelRecord,
    Error,
    { labelId: number; patch: { name?: string; color?: string } }
  >({
    mutationFn: (input) => updateTaskLabel(input.labelId, input.patch),
    onSettled: () => invalidateGlobalLabelCaches(queryClient),
  });
}

export function useDeleteTaskLabel() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (labelId) => deleteTaskLabel(labelId),
    onSettled: () => invalidateGlobalLabelCaches(queryClient),
  });
}
